import { ButtonHeader } from '../../../Header/Style';
import SVG from '../../../SVG';
import { Control, ControlContainer } from '../../../Controls/styled';
import {
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import Table from '../../../Table';
import useExportsController from '../useExportsController';
import { useAbility } from '../../../Hooks/useAbility';

const useTaxesController = () => {
  const {
    getInvoice,
    setInvoice,
    validateForm,
    setModal,
    getFormMessage,
    currentRoute,
    enqueueSnackbar,
    deleteTaxes: deleteTaxesAction,
    autocompleteTax,
    filterTax,
    createTax,
    updateTax,
  } = useExportsController();
  const { Can } = useAbility();

  const taxes = getInvoice('filters.taxes.data.data');

  const checkTaxIds = getInvoice('selecteds.taxes');

  const setCheckTaxIds = (selectedTaxIds: number[]) =>
    setInvoice('selecteds.taxes', selectedTaxIds);

  const allTaxChecks = taxes.every(({ id }) => checkTaxIds.includes(id));

  const taxCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckTaxIds(taxes.map(({ id }) => id));
    } else {
      setCheckTaxIds([]);
    }
  };

  const checkTaxId = (taxId: number) => {
    const newcheckIds = checkTaxIds.includes(taxId)
      ? checkTaxIds.filter((id) => id !== taxId)
      : [...checkTaxIds, taxId];
    setCheckTaxIds(newcheckIds);
  };

  const deleteTaxLoadings = getInvoice('loadings.deleteTaxes');

  const setDeleteTaxLoadings = (setDeleteTaxLoadings: boolean[]) =>
    setInvoice('loadings.deleteTaxes', setDeleteTaxLoadings);

  const deleteTaxes = (taxIds: number[] = checkTaxIds) =>
    new Promise((resolve, reject) => {
      const length = taxIds.length;

      const indexes = taxIds.map((taxId) =>
        taxes.findIndex(({ id }) => taxId === id),
      );

      setDeleteTaxLoadings(
        taxes.map((_, i) =>
          indexes.includes(i) ? true : deleteTaxLoadings[i],
        ),
      );

      deleteTaxesAction(taxIds)
        .then(() => {
          const newCheckIds = checkTaxIds.filter((id) => !taxIds.includes(id));
          setCheckTaxIds(newCheckIds);
          filterTax();
          resolve(true);
          enqueueSnackbar(
            length === 1
              ? 'A taxa foi excluída com sucesso'
              : 'As taxas foram excluídas com sucesso',
            {
              variant: 'success',
            },
          );
        })
        .catch(() => {
          reject(false);
          enqueueSnackbar(
            length === 1
              ? 'Houve uma falha ao excluir a taxa'
              : 'Houve uma falha ao excluir as taxas',
            {
              variant: 'error',
            },
          );
        })
        .finally(() => {
          setDeleteTaxLoadings(
            taxes.map((_, i) =>
              indexes.includes(i) ? false : deleteTaxLoadings[i],
            ),
          );
        });
    });

  const editTax = ({ id, name, value, description = '' }: Tax) => {
    setModal('single.updateTax', true);
    setInvoice('forms.taxes.update', {
      id,
      name,
      value,
      description,
    });
  };

  const searchFilter = getInvoice('filters.taxes.filter');
  const searchSelectFilter = getInvoice('filters.selectTaxes.filter');

  const setSearchFilter = (filter: SearchFilter) =>
    setInvoice('filters.taxes.filter', filter);

  const setSearchSelectFilter = (filter: SearchFilter) =>
    setInvoice('filters.selectTaxes.filter', filter);

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(field, e.target.value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);
  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = <
    T extends keyof InvoiceState['autocompletes']['taxes'],
  >(
    field: T,
  ) =>
    getInvoice(`autocompletes.taxes.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof InvoiceState['autocompletes']['taxes'],
  ) => {
    return autocompleteTax(field);
  };

  const setAutocompleteFilterField =
    (field: keyof InvoiceState['autocompletes']['taxes']) => (e) => {
      setInvoice(`autocompletes.taxes.${field}.filter.value`, e.target.value);
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const taxId = taxes[index].id;
    checkTaxId(taxId);
  };

  const hasCheckedTaxes = checkTaxIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setInvoice('filters.taxes.filter', {
      ...getInvoice('filters.taxes.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setInvoice('selecteds.taxes', []);
  };
  const TaxControls = () => {
    return currentRoute.includes('/tools/exports/taxes') ? (
      <>
        <Can I="create" a="Tax">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createTax', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVA TAXA
            </ButtonHeader>
          )}
        </Can>
        <ControlContainer>
          <Can I="delete" a="Tax">
            {() => (
              <Control
                active={hasCheckedTaxes}
                disabled={!hasCheckedTaxes}
                onClick={() => {
                  setModal('single.deleteTaxes', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedTaxes ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>

          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterTax', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };
  const total = getInvoice('filters.taxes.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };
  return {
    taxes,
    checkTaxIds,
    setCheckTaxIds,
    allTaxChecks,
    taxCheckAll,
    checkTaxId,
    deleteTaxes,
    deleteTaxLoadings,
    TaxControls,
    editTax,
    searchFilter,
    setSearchFilter,
    getFilterField,
    setFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setAutocompleteFilterField,
    setField,
    onLineClickHandler,
    filterTax,
    setInvoice,
    TableControl,
    getFormMessage,
    createTax,
    updateTax,
    getInvoice,
    setModal,
    validateForm,
    searchSelectFilter,
    setSearchSelectFilter,
  };
};

export default useTaxesController;
